import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import Card from "components/card";
import { useDealData } from "contexts/DealContext";
import { usePortfolioData } from "contexts/PortfolioContext";
import { useViewMode } from "contexts/ViewModeContext";
import { CompetitionSummaryTable, ConsolidatedCompetitionTable } from "./CompetitionTables";

const CompetitionChartPage = () => {
    const { chartType } = useParams();
    const [title, setTitle] = useState("");
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [renderKey, setRenderKey] = useState(0); // Key to force complete remount

    const {
        currentFacility,
        currentDeal,
        currentDealName,
        isDealLoading,
        competitionData
    } = useDealData();

    const {
        currentPortfolioFacility,
        currentPortfolio,
        currentPortfolioName,
        isPortfolioLoading,
        portfolioCompetitionData
    } = usePortfolioData();

    const { viewMode } = useViewMode();

    // Determine which data to use based on view mode
    const isPortfolioMode = viewMode === 'portfolio';
    const activeCompetitionData = isPortfolioMode ? portfolioCompetitionData : competitionData;

    // Handle naming mismatch: portfolio facility data uses "Crown" but competition data uses "Portfolio - Crown"
    const activeCurrentName = isPortfolioMode
        ? (currentPortfolioName ? `Portfolio - ${currentPortfolioName}` : null)
        : currentDealName;

    const activeCurrentFacility = isPortfolioMode ? currentPortfolioFacility : currentFacility;
    const activeCurrentDeal = isPortfolioMode ? currentPortfolio : currentDeal;
    const activeIsLoading = isPortfolioMode ? isPortfolioLoading : isDealLoading;

    // Get facility competition data
    const facilityCompData = useMemo(() => {
        if (!activeCompetitionData || !activeCurrentName || !activeCurrentFacility) return null;

        // Debug logging for portfolio mode
        if (isPortfolioMode) {
            console.log('🏥 Portfolio Competition Debug:', {
                portfolioCompetitionData: portfolioCompetitionData ? Object.keys(portfolioCompetitionData) : 'null',
                currentPortfolioName,
                activeCurrentName,
                hasData: !!activeCompetitionData[activeCurrentName]
            });
        }

        // Get facility CCN or identifier
        const facilityDetails = activeCurrentFacility.details || {};
        const facilityId = facilityDetails.CCN ||
            facilityDetails.ccn ||
            facilityDetails.ProviderNumber ||
            Object.keys(activeCompetitionData[activeCurrentName] || {})[0];

        if (!facilityId || facilityId === "consolidated") return null;

        // Find the deal/portfolio data
        const entityData = activeCompetitionData[activeCurrentName];
        if (!entityData) return null;

        // Find the facility data
        return entityData[facilityId] || null;
    }, [activeCompetitionData, activeCurrentName, activeCurrentFacility, isPortfolioMode, portfolioCompetitionData, currentPortfolioName]);

    // Calculate average values for competitors of a specific facility - EXACT COPY from original
    const facilityCompetitorAverage = useMemo(() => {
        if (!facilityCompData || !facilityCompData['closest facilities'] || facilityCompData['closest facilities'].length === 0) {
            return null;
        }

        const competitors = facilityCompData['closest facilities'].map(facility => facility['facility data']);

        const metrics = ['Star Rating', 'Health Inspec Rating', 'QM Rating', 'Staffing Rating',
            'Licensed Beds', 'Occupancy', 'ADC',
            'Health Survey Score', 'Percentile', 'Distance'];

        const sums = {};
        const counts = {};

        // Initialize sums and counts
        metrics.forEach(metric => {
            sums[metric] = 0;
            counts[metric] = 0;
        });

        // Sum up values from all competitors
        competitors.forEach(facility => {
            metrics.forEach(metric => {
                const value = facility[metric];
                if (value !== undefined && value !== null && !isNaN(Number(value))) {
                    sums[metric] += Number(value);
                    counts[metric]++;
                }
            });
        });

        // Calculate averages
        const averages = {};
        metrics.forEach(metric => {
            if (counts[metric] > 0) {
                if (['Star Rating', 'Health Inspec Rating', 'QM Rating', 'Staffing Rating'].includes(metric)) {
                    // Round ratings to nearest 0.5
                    averages[metric] = Math.round((sums[metric] / counts[metric]) * 2) / 2;
                } else {
                    averages[metric] = Math.round((sums[metric] / counts[metric]) * 100) / 100;
                }
            } else {
                averages[metric] = null;
            }
        });

        return averages;
    }, [facilityCompData]);

    // Get all facilities for the current deal/portfolio to calculate competitor averages
    const allFacilities = useMemo(() => {
        if (!activeCompetitionData || !activeCurrentName) return [];

        const entityData = activeCompetitionData[activeCurrentName];
        if (!entityData) return [];

        const allFacilitiesData = [];
        // Collect all facilities and their closest competitors
        Object.keys(entityData).forEach(facId => {
            const facData = entityData[facId];
            if (facData && facData['closest facilities']) {
                facData['closest facilities'].forEach(compFacility => {
                    if (compFacility['facility data']) {
                        allFacilitiesData.push(compFacility['facility data']);
                    }
                });
            }
        });

        return allFacilitiesData;
    }, [activeCompetitionData, activeCurrentName]);

    // Calculate average values for all competitor facilities
    const averageCompetitorValues = useMemo(() => {
        if (!allFacilities || allFacilities.length === 0) return null;

        const metrics = ['Star Rating', 'Health Inspec Rating', 'QM Rating', 'Staffing Rating',
            'Licensed Beds', 'Occupancy', 'ADC', 'Health Survey Score', 'Percentile'];

        const sums = {};
        const counts = {};

        // Initialize sums and counts
        metrics.forEach(metric => {
            sums[metric] = 0;
            counts[metric] = 0;
        });

        // Sum up all values
        allFacilities.forEach(facility => {
            metrics.forEach(metric => {
                const value = facility[metric];
                if (value !== undefined && value !== null && !isNaN(Number(value))) {
                    sums[metric] += Number(value);
                    counts[metric]++;
                }
            });
        });

        // Calculate averages
        const averages = {};
        metrics.forEach(metric => {
            if (counts[metric] > 0) {
                if (['Star Rating', 'Health Inspec Rating', 'QM Rating', 'Staffing Rating'].includes(metric)) {
                    // Round ratings to nearest 0.5
                    averages[metric] = Math.round((sums[metric] / counts[metric]) * 2) / 2;
                } else {
                    averages[metric] = Math.round((sums[metric] / counts[metric]) * 100) / 100;
                }
            } else {
                averages[metric] = null;
            }
        });

        return averages;
    }, [allFacilities]);

    // Calculate averages from portfolio/deal facilities in the competition data
    const entityAverages = useMemo(() => {
        if (!activeCompetitionData || !activeCurrentName) return {};

        // Get competition data for current deal/portfolio
        const entityCompData = activeCompetitionData[activeCurrentName];
        if (!entityCompData) return {};

        // Get all facilities from the competition data
        const entityFacilities = Object.values(entityCompData).map(facility => facility.data);

        if (entityFacilities.length === 0) return {};

        // Metrics to average
        const metrics = ['Licensed Beds', 'ADC', 'Health Survey Score', 'Percentile'];

        // Initialize sums and counts
        const sums = {};
        const counts = {};
        metrics.forEach(metric => {
            sums[metric] = 0;
            counts[metric] = 0;
        });

        // Sum up values from all entity facilities 
        entityFacilities.forEach(facility => {
            metrics.forEach(metric => {
                const value = facility[metric];
                if (value !== undefined && value !== null && !isNaN(Number(value))) {
                    sums[metric] += Number(value);
                    counts[metric]++;
                }
            });
        });

        // Calculate averages
        const averages = {};
        metrics.forEach(metric => {
            if (counts[metric] > 0) {
                averages[metric] = Math.round(sums[metric] / counts[metric]);
            } else {
                averages[metric] = null;
            }
        });

        return averages;
    }, [activeCompetitionData, activeCurrentName]);

    // Effect to reset everything on deal/portfolio change
    useEffect(() => {
        if (activeIsLoading) {
            // Create new render key to force complete remount
            setRenderKey(prev => prev + 1);
        }
    }, [activeIsLoading, activeCurrentName]);

    useEffect(() => {
        if (!chartType || !activeCurrentFacility || activeIsLoading) {
            // Skip re-rendering during loading states
            return;
        }

        // Check if current facility is a group
        const isGroup = activeCurrentFacility?.details?.isGroup;
        const groupPrefix = isGroup ? "Group " : "";

        // Set up the appropriate title based on the chart type
        switch (chartType) {
            case "competition-analysis":
                setTitle(`${groupPrefix}Competition Analysis`);
                break;
            case "market-comparison":
                setTitle(`${groupPrefix}Market Comparison`);
                break;
            default:
                setTitle("Competition Analysis");
        }

        // Mark initial load complete
        setIsInitialLoad(false);
    }, [chartType, activeCurrentFacility, activeCurrentDeal, activeCurrentName, activeIsLoading, activeCompetitionData]);

    // Show loading state if data isn't ready
    if (activeIsLoading || !activeCurrentFacility) {
        return (
            <Card extra={"w-full p-6"}>
                <div className="w-full flex justify-center items-center p-8">
                    <div className="text-gray-500">
                        {activeIsLoading ? `Loading competition data for selected ${isPortfolioMode ? 'portfolio' : 'deal'}...` : "Loading competition data..."}
                    </div>
                </div>
            </Card>
        );
    }

    // Check if we have a specific facility selected
    const isSpecificFacility = activeCurrentFacility &&
        Object.entries(activeCurrentDeal?.facilities || {}).find(
            ([name, facility]) =>
                facility === activeCurrentFacility &&
                name.toLowerCase() !== 'consolidated'
        );

    // Check if this is consolidated view
    const isConsolidatedView = !isSpecificFacility;

    // For consolidated view, show portfolio vs market comparison
    if (isConsolidatedView) {
        const consolidatedFacility = activeCurrentFacility?.details || {};

        return (
            <div key={`competition-chart-${renderKey}`}>
                <ConsolidatedCompetitionTable
                    portfolioAverages={entityAverages}
                    competitorAverages={averageCompetitorValues}
                    consolidatedFacility={consolidatedFacility}
                    currentDeal={activeCurrentDeal}
                />
            </div>
        );
    }

    // Show empty state if we don't have facility data for specific facility
    if (!facilityCompData || !activeCurrentName) {
        return (
            <Card extra={"w-full p-6"}>
                <h2 className="text-2xl font-bold mb-6 text-navy-700 dark:text-white">
                    {title || "Competition Analysis"}
                </h2>
                <div className="w-full flex justify-center items-center p-8">
                    <div className="text-gray-500">
                        No competition data available for this facility.
                    </div>
                </div>
            </Card>
        );
    }

    // Show individual facility competition table
    return (
        <div key={`competition-chart-${renderKey}`}>
            <CompetitionSummaryTable
                facilityData={facilityCompData.data}
                competitorData={facilityCompData['closest facilities']}
                facilityCompetitorAverage={facilityCompetitorAverage}
            />
        </div>
    );
};

export default CompetitionChartPage; 